import { useMutation, useQuery } from "@tanstack/react-query";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

export type JobListing = {
  id: string;
  source: string;
  externalId: string;
  title: string;
  company: string;
  location: string | null;
  remote: boolean;
  url: string;
  description: string;
  tags: string[];
  salary: string | null;
  publishedAt: string | null;
  createdAt: string;
};

export type JobList = { items: JobListing[]; total: number };

export const fetchJobs = async (params: { query?: string; remote?: boolean; take?: number }) => {
  const response = await axios.get<JobList>("/jobs", {
    params: {
      query: params.query || undefined,
      remote: params.remote ? "true" : undefined,
      take: params.take ?? undefined,
    },
  });

  return response.data;
};

export const useJobs = (params: { query?: string; remote?: boolean; take?: number }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["jobs", params],
    queryFn: () => fetchJobs(params),
  });

  return { jobs: data?.items, total: data?.total, loading: isLoading };
};

export type SearchResult = { found: number; saved: number; sources: string[] };

export const searchJobs = async (payload: { query: string; location?: string }) => {
  const response = await axios.post<SearchResult>("/jobs/search", payload);

  return response.data;
};

export const useSearchJobs = () => {
  const { mutateAsync: search, isPending: loading } = useMutation({
    mutationFn: searchJobs,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
  });

  return { search, loading };
};

// Followed searches (autonomous scraping profiles).
export type SearchProfile = {
  id: string;
  keywords: string;
  location: string | null;
  active: boolean;
  lastRunAt: string | null;
  lastNewCount: number;
  totalMatches: number;
  createdAt: string;
};

const invalidateProfiles = () => {
  void queryClient.invalidateQueries({ queryKey: ["job-profiles"] });
  void queryClient.invalidateQueries({ queryKey: ["jobs"] });
};

export const fetchProfiles = async () => {
  const response = await axios.get<SearchProfile[]>("/jobs/profiles");
  return response.data;
};

export const useProfiles = () => {
  const { data, isLoading } = useQuery({ queryKey: ["job-profiles"], queryFn: fetchProfiles });
  return { profiles: data, loading: isLoading };
};

export const followSearch = async (payload: { keywords: string; location?: string }) => {
  const response = await axios.post<SearchProfile>("/jobs/profiles", payload);
  return response.data;
};

export const useFollowSearch = () => {
  const { mutateAsync: follow, isPending: loading } = useMutation({
    mutationFn: followSearch,
    onSuccess: invalidateProfiles,
  });
  return { follow, loading };
};

export const runProfile = async (id: string) => {
  const response = await axios.post<SearchProfile>(`/jobs/profiles/${id}/run`);
  return response.data;
};

export const useRunProfile = () => {
  const { mutateAsync: run, isPending: loading } = useMutation({
    mutationFn: runProfile,
    onSuccess: invalidateProfiles,
  });
  return { run, loading };
};

export const toggleProfile = async (payload: { id: string; active: boolean }) => {
  const response = await axios.patch<SearchProfile>(`/jobs/profiles/${payload.id}`, {
    active: payload.active,
  });
  return response.data;
};

export const useToggleProfile = () => {
  const { mutateAsync: toggle } = useMutation({
    mutationFn: toggleProfile,
    onSuccess: invalidateProfiles,
  });
  return { toggle };
};

export const deleteProfile = async (id: string) => {
  await axios.delete(`/jobs/profiles/${id}`);
  return id;
};

export const useDeleteProfile = () => {
  const { mutateAsync: remove } = useMutation({
    mutationFn: deleteProfile,
    onSuccess: (id) => {
      queryClient.setQueryData<SearchProfile[]>(["job-profiles"], (cache) =>
        cache?.filter((profile) => profile.id !== id),
      );
    },
  });
  return { remove };
};

// Alerts: new offers found by autonomous runs the user hasn't seen yet.
export type JobAlert = {
  id: string;
  createdAt: string;
  job: JobListing;
  profile: { id: string; keywords: string };
};

export const fetchAlerts = async () => {
  const response = await axios.get<JobAlert[]>("/jobs/alerts");
  return response.data;
};

export const useAlerts = () => {
  const { data } = useQuery({ queryKey: ["job-alerts"], queryFn: fetchAlerts });
  return { alerts: data };
};

export const fetchAlertsCount = async () => {
  const response = await axios.get<number>("/jobs/alerts/count");
  return response.data;
};

export const useAlertsCount = () => {
  const { data } = useQuery({
    queryKey: ["job-alerts-count"],
    queryFn: fetchAlertsCount,
    refetchInterval: 60_000,
  });
  return { count: data ?? 0 };
};

export type PreparedField = {
  key: string;
  label: string;
  type: "text" | "textarea" | "select" | "file";
  required: boolean;
  options?: string[];
  identity?: string;
  value: string;
};

export type PreparedApplication = {
  supported: boolean;
  source: string;
  jobTitle: string;
  company: string;
  url: string;
  fields: PreparedField[];
};

export const prepareApplication = async (payload: { jobId: string; resumeId: string }) => {
  const response = await axios.post<PreparedApplication>(
    `/jobs/${payload.jobId}/application-draft`,
    { resumeId: payload.resumeId },
  );
  return response.data;
};

export const usePrepareApplication = () => {
  const { mutateAsync: prepare, isPending: loading } = useMutation({
    mutationFn: prepareApplication,
  });
  return { prepare, loading };
};

export type DigestItem = { job: JobListing; fit: number };

export const fetchDigest = async () => {
  const response = await axios.get<DigestItem[]>("/jobs/digest");
  return response.data;
};

export const useDigest = () => {
  const { data, isLoading } = useQuery({ queryKey: ["job-digest"], queryFn: fetchDigest });
  return { digest: data, loading: isLoading };
};

export const markAlertsRead = async () => {
  await axios.post("/jobs/alerts/read");
};

export const useMarkAlertsRead = () => {
  const { mutateAsync: markRead } = useMutation({
    mutationFn: markAlertsRead,
    onSuccess: () => {
      queryClient.setQueryData(["job-alerts-count"], 0);
    },
  });
  return { markRead };
};
